/**
 * WorkloadHeatMap
 *
 * GitHub-contributions style heat map showing task density over the last N days.
 * Grid: 13 columns (weeks) x 7 rows (days).
 * Each cell colored by task count using accent-violet color scale.
 */

import { useMemo } from 'react';
import { subDays, startOfDay, format } from 'date-fns';
import { it } from 'date-fns/locale';
import clsx from 'clsx';
import type { Task } from '@/db';

interface WorkloadHeatMapProps {
  tasks: (Task & { id: string })[];
  days?: number;
}

interface DayCell {
  date: Date;
  count: number;
}

function getIntensityClass(count: number): string {
  if (count === 0) return 'bg-surface-tertiary/40';
  if (count <= 2) return 'bg-accent-violet/20';
  if (count <= 4) return 'bg-accent-violet/45';
  return 'bg-accent-violet/80';
}

function getIntensityLabel(count: number): string {
  if (count === 0) return 'Nessun task';
  if (count === 1) return '1 task';
  return `${count} task`;
}

export function WorkloadHeatMap({ tasks, days = 91 }: WorkloadHeatMapProps) {
  const { cells, weeks } = useMemo(() => {
    const today = startOfDay(new Date());
    // Build map of date -> task count based on due date
    const dateCountMap = new Map<string, number>();

    tasks.forEach((task) => {
      if (!task.dueDate) return;
      const dateKey = startOfDay(new Date(task.dueDate)).toISOString();
      dateCountMap.set(dateKey, (dateCountMap.get(dateKey) || 0) + 1);
    });

    // Generate cells for the last N days
    const allCells: DayCell[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(today, i);
      const dateKey = startOfDay(date).toISOString();
      allCells.push({
        date,
        count: dateCountMap.get(dateKey) || 0,
      });
    }

    // Pad start to align with Monday (weekday 1)
    const firstDay = allCells[0].date.getDay();
    // Convert Sunday (0) to 7 for Monday-first week
    const dayOffset = firstDay === 0 ? 6 : firstDay - 1;
    const paddedCells: (DayCell | null)[] = [];
    for (let i = 0; i < dayOffset; i++) {
      paddedCells.push(null);
    }
    paddedCells.push(...allCells);

    // Group into weeks (columns of 7)
    const weekGroups: (DayCell | null)[][] = [];
    for (let i = 0; i < paddedCells.length; i += 7) {
      weekGroups.push(paddedCells.slice(i, i + 7));
    }

    // Pad last week to 7 days
    const lastWeek = weekGroups[weekGroups.length - 1];
    if (lastWeek && lastWeek.length < 7) {
      while (lastWeek.length < 7) {
        lastWeek.push(null);
      }
    }

    return { cells: allCells, weeks: weekGroups };
  }, [tasks, days]);

  const totalTasks = cells.reduce((sum, c) => sum + c.count, 0);
  const dayLabels = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

  return (
    <div className="bg-surface-secondary/30 rounded-xl border border-border-primary p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[13px] font-semibold text-text-primary">Mappa attivita</h4>
        <span className="text-[10px] text-text-quaternary">
          {totalTasks} task negli ultimi {days} giorni
        </span>
      </div>

      <div className="flex gap-1">
        {/* Day labels */}
        <div className="flex flex-col gap-[3px] mr-1">
          {dayLabels.map((label, i) => (
            <div
              key={i}
              className="w-3 h-3 flex items-center justify-center text-[8px] text-text-quaternary"
            >
              {i % 2 === 0 ? label : ''}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="flex gap-[3px] overflow-x-auto">
          {weeks.map((week, wi) => (
            <div key={wi} className="flex flex-col gap-[3px]">
              {week.map((cell, di) => (
                <div
                  key={`${wi}-${di}`}
                  className={clsx(
                    'w-3 h-3 rounded-[2px] transition-colors',
                    cell ? getIntensityClass(cell.count) : 'bg-transparent'
                  )}
                  title={
                    cell
                      ? `${format(cell.date, 'd MMM yyyy', { locale: it })}: ${getIntensityLabel(cell.count)}`
                      : undefined
                  }
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-1.5 mt-3 justify-end">
        <span className="text-[9px] text-text-quaternary">Meno</span>
        <div className="w-3 h-3 rounded-[2px] bg-surface-tertiary/40" />
        <div className="w-3 h-3 rounded-[2px] bg-accent-violet/20" />
        <div className="w-3 h-3 rounded-[2px] bg-accent-violet/45" />
        <div className="w-3 h-3 rounded-[2px] bg-accent-violet/80" />
        <span className="text-[9px] text-text-quaternary">Di piu</span>
      </div>
    </div>
  );
}
